import { containsTextCaseInsensitive } from 'lib/utils/strings';
import {
  WORK_ORDER_OPERATION_FIELDS,
  parseSearchFields,
  isBarcodeFormattedSearch,
} from 'lib/utils/workOrderOperationSearch';

const baseWhere = {
  closedDate: { _is_null: true },
  status: { _nin: ['C', 'X', 'COMPLETED', 'NOT RELEASED'] },
};

const getByJobWhereClause = (job) => {
  const jobSequence = job.operation;

  const conditions = [];

  if (job.name) {
    conditions.push({ partNumber: { _ilike: `%${job.name}%` } });
  }
  if (job.part) {
    conditions.push({ partNumber: { _ilike: `%${job.part}%` } });
  }

  return {
    ...baseWhere,
    ...(jobSequence ? { sequenceNumber: { _eq: jobSequence } } : {}),
    workOrder: {
      part: conditions.length > 0 ? { _or: conditions } : {},
    },
  };
};

export const getByPartsWhere = ({
  latestJob,
  isAPMEnabled,
  orderOperation,
}) => {
  const partOperationRef = orderOperation?.partOperationRef;

  if (isAPMEnabled && !partOperationRef) {
    return getByJobWhereClause(latestJob);
  }

  if (!orderOperation) {
    return baseWhere;
  }

  return {
    ...baseWhere,
    sequenceNumber: { _eq: orderOperation.sequenceNumber },
    workOrder: {
      part: {
        partOperations: {
          partOperationRef: { _eq: partOperationRef },
        },
      },
    },
  };
};

/**
 * Build GraphQL where clause from parsed work order operation fields
 *
 * @param {Object} parsedFields - Parsed field values from parseSearchFields()
 * @param {Function} trimFn - Optional trim function for values
 * @returns {Object} GraphQL where clause with _and conditions
 *
 * @example
 * buildGraphQLWhereClause({ workOrderId: "123", sequenceNumber: "10" }, trim)
 * // Returns: { _and: [{ workOrderId: { _ilike: "%123%" }}, { sequenceNumber: { _eq: 10 }}]}
 */
export const buildGraphQLWhereClause = (
  parsedFields,
  trimFn = (val) => {
    return val;
  },
  { caseInsensitive = false } = {}
) => {
  const andConditions = [];

  // Barcode-scanned values are exact; operator-typed display-name values may differ in case
  // (e.g. "job123-a op20"), so those match case-insensitively via _ilike on the exact value.
  const stringMatch = (value) => {
    return caseInsensitive ? { _ilike: value } : { _eq: value };
  };

  // workOrderId - use partial match (ILIKE)
  if (parsedFields[WORK_ORDER_OPERATION_FIELDS.WORK_ORDER_ID]) {
    andConditions.push({
      workOrderId: {
        _ilike: `%${trimFn(
          parsedFields[WORK_ORDER_OPERATION_FIELDS.WORK_ORDER_ID]
        )}%`,
      },
    });
  }

  // partNumber - partial match on the related work order's part number. Only set by the
  // composed display-name parser (parseDisplayNameSearch); the barcode parser never emits it.
  if (parsedFields.partNumber) {
    andConditions.push({
      workOrder: {
        partNumber: { _ilike: `%${trimFn(parsedFields.partNumber)}%` },
      },
    });
  }

  // sequenceNumber - use exact match (numeric)
  if (parsedFields[WORK_ORDER_OPERATION_FIELDS.SEQUENCE_NUMBER]) {
    const sequenceNum = Number.parseInt(
      parsedFields[WORK_ORDER_OPERATION_FIELDS.SEQUENCE_NUMBER],
      10
    );
    if (!Number.isNaN(sequenceNum)) {
      andConditions.push({
        sequenceNumber: { _eq: sequenceNum },
      });
    }
  }

  // sequenceNumberPrefix - the typed digits may be an incomplete operation number, so match
  // them as a numeric prefix (10 -> 10, 100-109, 1000-1099). Only set by the composed
  // display-name parser; the barcode parser never emits it.
  if (
    parsedFields.sequenceNumberPrefix &&
    /^\d+$/.test(String(parsedFields.sequenceNumberPrefix))
  ) {
    const prefix = String(parsedFields.sequenceNumberPrefix);
    const base = Number.parseInt(prefix, 10);
    const ranges = [{ sequenceNumber: { _eq: base } }];
    // Operation numbers are small integers; cover prefix extensions up to 5 digits total.
    for (let extra = 1; prefix.length + extra <= 5; extra += 1) {
      const lo = base * 10 ** extra;
      ranges.push({ sequenceNumber: { _gte: lo, _lte: lo + 10 ** extra - 1 } });
    }
    andConditions.push({ _or: ranges });
  }

  // lot - exact match (case-insensitive for display-name-derived searches)
  if (parsedFields[WORK_ORDER_OPERATION_FIELDS.LOT]) {
    andConditions.push({
      lot: stringMatch(parsedFields[WORK_ORDER_OPERATION_FIELDS.LOT]),
    });
  }

  // sub - exact match (case-insensitive for display-name-derived searches)
  if (parsedFields[WORK_ORDER_OPERATION_FIELDS.SUB]) {
    andConditions.push({
      sub: stringMatch(parsedFields[WORK_ORDER_OPERATION_FIELDS.SUB]),
    });
  }

  // split - exact match (case-insensitive for display-name-derived searches)
  if (parsedFields[WORK_ORDER_OPERATION_FIELDS.SPLIT]) {
    andConditions.push({
      split: stringMatch(parsedFields[WORK_ORDER_OPERATION_FIELDS.SPLIT]),
    });
  }

  // Return _and clause if we have conditions, empty object otherwise
  if (andConditions.length > 0) {
    return { _and: andConditions };
  }

  return {};
};

export const searchOnProductionScheduleOperations = (
  search,
  operations,
  getOrderOpDisplayName
) => {
  if (!search || !operations) {
    return operations;
  }

  // Check if search has barcode field prefixes
  const isBarcodeFormat = isBarcodeFormattedSearch(search);
  const parsedFields = isBarcodeFormat ? parseSearchFields(search) : {};
  // Only take the AND-based barcode path when the search actually parsed into known fields.
  // Otherwise (e.g. a display-name format whose literal contains ': ') fall through to the
  // plain-text / composed-name match rather than matching every operation.
  const useBarcodeMatch = Object.keys(parsedFields).length > 0;

  return operations.filter((workOrderOperation) => {
    if (useBarcodeMatch) {
      // AND-based filtering for barcode searches
      let matches = true;

      // All fields must match (AND logic)
      if (parsedFields.workOrderId) {
        matches =
          matches &&
          containsTextCaseInsensitive(
            workOrderOperation?.workOrder?.workOrderId,
            parsedFields.workOrderId
          );
      }

      // Check sequenceNumber - exact match
      if (parsedFields.sequenceNumber) {
        const sequenceNum = Number.parseInt(parsedFields.sequenceNumber, 10);
        matches = matches && workOrderOperation?.sequenceNumber === sequenceNum;
      }

      // Check lot - exact match
      if (parsedFields.lot) {
        matches = matches && workOrderOperation?.lot === parsedFields.lot;
      }

      // Check sub - exact match
      if (parsedFields.sub) {
        matches = matches && workOrderOperation?.sub === parsedFields.sub;
      }

      // Check split - exact match
      if (parsedFields.split) {
        matches = matches && workOrderOperation?.split === parsedFields.split;
      }

      return matches;
    }

    // Use original OR-based text search for plain text
    return (
      containsTextCaseInsensitive(
        workOrderOperation?.workOrder?.partNumber,
        search
      ) ||
      containsTextCaseInsensitive(
        workOrderOperation?.workOrder?.workOrderId,
        search
      ) ||
      containsTextCaseInsensitive(workOrderOperation?.lot, search) ||
      containsTextCaseInsensitive(workOrderOperation?.split, search) ||
      containsTextCaseInsensitive(workOrderOperation?.sub, search) ||
      // Match the FULL card title the operator actually sees — "workOrderId | displayName"
      // (JobCardTitle's search-mode header) — so any substring of it, pipes included, finds
      // the card. getOrderOpDisplayName is optional so callers that don't have the company
      // format handy keep the field-only behavior.
      (getOrderOpDisplayName &&
        containsTextCaseInsensitive(
          [
            workOrderOperation?.workOrder?.workOrderId ||
              workOrderOperation?.workOrderId,
            getOrderOpDisplayName(workOrderOperation),
          ]
            .filter(Boolean)
            .join(' | '),
          search
        ))
    );
  });
};

// trim whitespace from start and end of string
// and remove leading zeroes
// samples:
// ' 123 ' -> '123'
// '00123' -> '123'
// '123' -> '123'
// '000' -> ''
// '  123  ' -> '123'
// '000123000' -> '123'
export const trim = (value) => {
  try {
    return value.replace(/^[\s0]+/, '').replace(/\s+$/, '');
  } catch {
    return value;
  }
};

export const provideFallbackForCycleTimeMs = (operations) => {
  return operations.map((operation) => {
    const partOperationCycleTimeMs =
      operation.matchingPartOperations[0]?.cycleTimeMs || 0;
    return {
      ...operation,
      // if no operation cycleTimeMs, use cycleTimeMs on partOperation
      cycleTimeMs: operation.cycleTimeMs || partOperationCycleTimeMs,
    };
  });
};

/**
 * The work order operation of the board card that holds a program event, or
 * null for an unknown card or one no longer on the board.
 */
export const findCardWooRefForEvent = (programEvents, eventId) => {
  if (eventId == null) {
    return null;
  }
  const card = (programEvents || []).find((event) => {
    return (event.intervals || []).some((interval) => {
      return String(interval.programEventRef) === String(eventId);
    });
  });
  return card?.workOrderOperationRef ?? null;
};

// The split destination the form reads off the draft. Shared by both job
// pickers. matchingPartOperations is empty when the ERP maps no part. The
// production schedule sends none, and puts the part operation fields on the op.
export const buildSplitDestination = (workOrderOp) => {
  const partOp =
    workOrderOp?.matchingPartOperations?.length > 0
      ? workOrderOp.matchingPartOperations[0]
      : workOrderOp;

  return {
    workOrderOperationRef: workOrderOp?.workOrderOperationRef ?? null,
    workOrderId: workOrderOp?.workOrderId ?? null,
    partOperationRef: partOp?.partOperationRef ?? null,
    partNumber:
      partOp?.partNumber ?? workOrderOp?.workOrder?.partNumber ?? null,
    sequenceNumber:
      partOp?.sequenceNumber ?? workOrderOp?.sequenceNumber ?? null,
    finishQuantity:
      workOrderOp?.finishQuantity ?? workOrderOp?.quantityScheduled ?? null,
    // The standards useInitialData stores on currentWorkOrderOp, read the same
    // way, because the split writes this destination there.
    cycleTimeMs: workOrderOp?.cycleTimeMs || partOp?.cycleTimeMs || null,
    setupTimeMs: workOrderOp?.setupTimeMs ?? partOp?.setupTimeMs ?? null,
    quantityPerPart: partOp?.quantityPerPart > 0 ? partOp.quantityPerPart : 1,
    lot: workOrderOp?.lot ?? workOrderOp?.workOrder?.lot ?? null,
    sub: workOrderOp?.sub ?? workOrderOp?.workOrder?.sub ?? null,
    split: workOrderOp?.split ?? workOrderOp?.workOrder?.split ?? null,
  };
};

// A destination pick is live only while the draft is. Route state survives on
// the history entry, so a Back into the picker would otherwise still bypass the
// open-run guard after the split has finished.
export const isLiveSplitDestinationPick = (routeState, splitDraft) => {
  return Boolean(routeState?.isSplitDestinationPick && splitDraft?.splitTime);
};
